import { useCallback } from "react";
import { useURLState } from "./useURLState";
import { getDetailPath } from "../utils/navigationUtils";
import { CREATIVE_ROUTE_CONFIG } from "../config/routeConfig";

/**
 * Simplified navigation hook for the Creative system
 * Provides clean API for all navigation operations
 */
export const useCreativeNavigation = () => {
  const { urlState, updateURL, history, location } = useURLState();

  // Single navigation function - replaces navigateToTab, navigateToPage, etc.
  const navigate = useCallback((options = {}) => {
    const {
      tab = urlState.tab,
      subtab = urlState.subtab,
      page = urlState.pageNumber,
      type = 'replace',
      detail = null
    } = options;

    if (detail) {
      // Handle detail navigation with return URL using RESTful pattern
      const { type, id, mediaType = 'DISPLAY' } = detail;
      const detailPath = getDetailPath(type, mediaType);

      const currentUrl = `${location.pathname}${location.search}`;
      const returnUrl = encodeURIComponent(currentUrl);
      history.push(`${detailPath}/${id}?returnUrl=${returnUrl}`);
    } else {
      // Handle tab/page navigation
      updateURL({ tab, subtab, page }, { type });
    }
  }, [urlState, updateURL, history, location]);

  // Legacy compatibility functions (use navigate() for new code)
  const navigateToTab = useCallback((tab, subtab, page = 1) => {
    navigate({ tab, subtab, page });
  }, [navigate]);

  const navigateToPage = useCallback((page) => {
    navigate({ page });
  }, [navigate]);

  const navigateToDetail = useCallback((detailType, id, mediaType = 'DISPLAY') => {
    navigate({ detail: { type: detailType, id, mediaType } });
  }, [navigate]);

  // Smart back navigation with context preservation
  const navigateBack = useCallback((fallbackContext = {}) => {
    const { returnUrl } = urlState;

    if (returnUrl) {
      history.push(decodeURIComponent(returnUrl));
    } else {
      // Intelligent fallback based on context
      const { mediaType = 'DISPLAY', isAdGroup = false } = fallbackContext;
      const tab = mediaType === 'VIDEO' ? 'videoAds' : 'displayAds';
      const subtab = isAdGroup ?
        (mediaType === 'VIDEO' ? 'adVideoGroups' : 'adGroups') :
        (mediaType === 'VIDEO' ? 'videos' : 'displayAdsUnits');

      navigate({ tab, subtab, page: 1 });
    }
  }, [urlState, history, navigate]);

  // Simple route validation (moved complex logic to useRouteValidation)
  const validateRoute = useCallback(() => {
    const { tab, subtab, pageNumber } = urlState;
    const isValid = CREATIVE_ROUTE_CONFIG.validTabs.includes(tab) &&
                   CREATIVE_ROUTE_CONFIG.validSubtabs[tab]?.includes(subtab) &&
                   pageNumber > 0;

    if (!isValid) {
      const { tab: defaultTab, subtab: defaultSubtab, page: defaultPage } = CREATIVE_ROUTE_CONFIG.defaultRoute;
      navigate({ tab: defaultTab, subtab: defaultSubtab, page: defaultPage });
      return false;
    }

    return true;
  }, [urlState, navigate]);

  // Get navigation helpers for current context
  const getNavigationHelpers = useCallback(() => {
    const { tab, subtab } = urlState;
    return {
      isDisplayAds: tab === 'displayAds',
      isVideoAds: tab === 'videoAds',
      isAdUnits: subtab.includes('Units') || subtab === 'videos',
      isAdGroups: subtab.includes('Groups') || subtab.includes('adGroups'),
      mediaType: tab === 'videoAds' ? 'VIDEO' : 'DISPLAY'
    };
  }, [urlState]);

  return {
    // Current state (renamed for clarity)
    currentState: urlState,

    // Primary navigation function
    navigate,

    // Legacy navigation functions (backward compatibility)
    navigateToTab,
    navigateToPage,
    navigateToDetail,
    navigateBack,

    // Validation
    validateRoute,

    // Helpers
    getNavigationHelpers,

    // Legacy compatibility
    updateUrlWithTabState: navigateToTab,
    updateUrlWithPagination: navigateToPage
  };
};

export default useCreativeNavigation;